import { useMemo } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Check,
  FileText,
  Lightbulb,
  Linkedin,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { isValidLinkedInProfileUrl } from '../../utils/url';
import { profileStrengthLabel, resolveOnboardingProfileStrength } from './onboardingScoring';
import type { OnboardingDraft, ScreeningPromptItem } from './types';

type LinkedInStageProps = {
  draft: OnboardingDraft | null;
  linkedin: string;
  resumeName: string;
  resumeUrl?: string;
  targetRole: string;
  initials: string;
  saving: boolean;
  sessionUser: Record<string, unknown> | null;
  promptItems: ScreeningPromptItem[];
  videoFiles: Record<number, File>;
  onLinkedinChange: (value: string) => void;
  onBack: () => void;
  onContinue: () => void;
};

function StrengthRing({ percent, label }: { percent: number; label: string }) {
  const circumference = 2 * Math.PI * 60;
  const dash = (percent / 100) * circumference;

  return (
    <svg width="140" height="140" viewBox="0 0 140 140" aria-hidden>
      <circle cx="70" cy="70" r="60" fill="none" stroke="#eef0f2" strokeWidth="10" />
      <circle
        cx="70"
        cy="70"
        r="60"
        fill="none"
        stroke="#198754"
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform="rotate(-90 70 70)"
      />
      <text x="70" y="66" textAnchor="middle" fontSize="26" fontWeight="700" fill="#1f2a24">
        {percent}%
      </text>
      <text x="70" y="86" textAnchor="middle" fontSize="12" fill="#6b7280">
        {label}
      </text>
    </svg>
  );
}

export default function LinkedInStage({
  draft,
  linkedin,
  resumeName,
  resumeUrl,
  targetRole,
  initials,
  saving,
  sessionUser,
  promptItems,
  videoFiles,
  onLinkedinChange,
  onBack,
  onContinue,
}: LinkedInStageProps) {
  const linkedinInvalid = Boolean(linkedin.trim()) && !isValidLinkedInProfileUrl(linkedin);
  const canContinue = !saving && !linkedinInvalid;

  const checklist = useMemo(
    () => ({
      hasExperience: Boolean(draft?.workExperience?.length),
      hasSkills: Boolean(draft?.skills?.length),
      hasProjects: Boolean(draft?.projects?.length),
      hasLinkedin: Boolean(linkedin.trim()) && isValidLinkedInProfileUrl(linkedin),
    }),
    [draft, linkedin]
  );

  const strengthPercent = useMemo(() => {
    const readiness = resolveOnboardingProfileStrength(
      sessionUser,
      draft,
      targetRole,
      linkedin,
      resumeUrl,
      promptItems,
      videoFiles
    );
    return Math.max(0, Math.min(100, Math.round(readiness.percent)));
  }, [sessionUser, draft, targetRole, linkedin, resumeUrl, promptItems, videoFiles]);

  const strengthText = profileStrengthLabel(strengthPercent);

  const roleLabel = targetRole || draft?.targetRole || draft?.currentJobTitle || 'Your role';
  const prefilledFromResume = Boolean(
    linkedin.trim() && draft?.contact?.linkedin && linkedin.trim() === draft.contact.linkedin.trim()
  );

  return (
    <>
      <div className="ssr-body ssr-body--linkedin">
        <div className="ssr-left">
          <div className="ssr-card">
            <p className="ssr-strength-title">Profile strength</p>
            <div className="ssr-progress-wrap">
              <StrengthRing percent={strengthPercent} label={strengthText} />
            </div>
            <div className="ssr-checklist">
              <div className="ssr-checklist-item">
                <span className="ssr-check-icon done">
                  <Check size={12} strokeWidth={3} />
                </span>
                Resume uploaded
              </div>
              <div className="ssr-checklist-item">
                <span className={`ssr-check-icon ${checklist.hasExperience ? 'done' : 'pending'}`}>
                  {checklist.hasExperience ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                Experience detected
              </div>
              <div className="ssr-checklist-item">
                <span className={`ssr-check-icon ${checklist.hasSkills ? 'done' : 'pending'}`}>
                  {checklist.hasSkills ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                Skills added
              </div>
              <div className={`ssr-checklist-item ${checklist.hasLinkedin ? '' : 'pending-text'}`}>
                <span className={`ssr-check-icon ${checklist.hasLinkedin ? 'done' : 'pending'}`}>
                  {checklist.hasLinkedin ? <Check size={12} strokeWidth={3} /> : null}
                </span>
                LinkedIn added
              </div>
            </div>
          </div>

          <div className="ssr-tip-card">
            <div className="ssr-tip-title">
              <Lightbulb size={16} color="#198754" />
              Tip
            </div>
            <p className="ssr-tip-body">
              Adding LinkedIn helps recruiters verify your background and can improve profile
              visibility.
            </p>
          </div>
        </div>

        <div className="ssr-center">
          <div className="ssr-card ssr-banner">
            <span className="ssr-banner-icon">
              <Check size={16} strokeWidth={3} />
            </span>
            <div>
              <h2 className="ssr-heading">Resume parsed successfully</h2>
              <p>
                We drafted your profile from <strong>{resumeName || 'your resume'}</strong>. Add
                LinkedIn to strengthen your profile, or skip and continue to review.
              </p>
            </div>
          </div>

          <div className="ssr-card ssr-linkedin-card">
            <div className="ssr-linkedin-head">
              <div className="ssr-linkedin-icon-wrap" aria-hidden>
                <Linkedin size={22} />
              </div>
              <div>
                <h3 className="ssr-heading">LinkedIn profile</h3>
                <p className="ssr-linkedin-sub">
                  Optional — helps recruiters find and trust your profile.
                </p>
              </div>
            </div>

            <div className="ssr-linkedin-preview">
              <div className="ssr-linkedin-avatar">{initials}</div>
              <div>
                <p className="ssr-linkedin-role">{roleLabel}</p>
                <p className="ssr-linkedin-meta">
                  <Briefcase size={14} />
                  Draft generated from your resume
                </p>
              </div>
            </div>

            <label className="ssr-linkedin-label" htmlFor="fast-onboarding-linkedin">
              Profile URL
            </label>
            <div className={`ssr-linkedin-input-wrap ${linkedinInvalid ? 'is-invalid' : ''}`}>
              <Linkedin size={18} className="ssr-linkedin-input-icon" aria-hidden />
              <input
                id="fast-onboarding-linkedin"
                type="url"
                className="ssr-linkedin-input"
                placeholder="https://linkedin.com/in/your-name"
                value={linkedin}
                onChange={(event) => onLinkedinChange(event.target.value)}
              />
            </div>

            {prefilledFromResume ? (
              <p className="ssr-linkedin-hint ssr-linkedin-hint--success">
                <Sparkles size={14} />
                Pre-filled from your resume. You can edit or clear this field.
              </p>
            ) : (
              <p className="ssr-linkedin-hint">
                <FileText size={14} />
                Paste your public LinkedIn URL. Leave blank to skip this step.
              </p>
            )}

            {linkedinInvalid ? (
              <p className="ssr-linkedin-error">
                Enter a valid LinkedIn profile URL or leave blank.
              </p>
            ) : null}
          </div>
        </div>

        <div className="ssr-right">
          <div className="ssr-card ssr-linkedin-benefits">
            <p className="ssr-linkedin-benefits-title ssr-heading">Why add LinkedIn?</p>
            <ul className="ssr-linkedin-benefits-list">
              <li>Recruiters can cross-check your experience faster</li>
              <li>Improves trust when your profile is shared</li>
              <li>Takes less than a minute to add</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="ssr-footer">
        <button type="button" className="ssr-back-btn" onClick={onBack} disabled={saving}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="ssr-footer-note">
          <ShieldCheck size={16} />
          LinkedIn is optional — you can add it later
        </div>
        <div className="ssr-footer-actions">
          <button
            type="button"
            className="ssr-edit-profile-btn"
            onClick={onContinue}
            disabled={!canContinue}
          >
            Skip for now
          </button>
          <button
            type="button"
            className="ssr-publish-btn"
            onClick={onContinue}
            disabled={!canContinue}
          >
            {saving ? 'Saving…' : 'Continue to review'} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </>
  );
}
